import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtPct } from "../lib/format";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { PageHeader } from "../components/PageHeader";
import { IconTruck, IconShield, IconAlertTriangle } from "../components/icons";
import type { TransportInsuranceDto } from "@bi/types";

/** taxa já vem em escala percentual (0,30 = 0,30%). 3 casas p/ taxas pequenas. */
const pctRate = (v: number | null) => (v == null ? "—" : fmtPct(v, 3));

export function InsurancePage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading } = useQuery({
    queryKey: ["transport", "insurance", from, to],
    queryFn: () =>
      api.get<TransportInsuranceDto>(
        `/dashboards/transport/insurance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const byPlate = data?.byPlate ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Seguro de Carga"
        subtitle="Exposição = valor da mercadoria × taxa da apólice, comparada ao seguro pago"
      />

      {/* Banner: taxa de referência ou apólice não configurada */}
      {data && data.rateSource === "none" && (
        <div className="card px-5 py-3 flex items-start gap-3 text-sm text-gray-600">
          <IconAlertTriangle className="w-4 h-4 text-warning-500 flex-shrink-0 mt-0.5" />
          <p>Nenhuma apólice configurada — o seguro estimado não pode ser calculado. Configure a taxa
            (%) da apólice de carga para ver a exposição por placa.</p>
        </div>
      )}
      {data && data.isReference && (
        <div className="card px-5 py-3 flex items-start gap-3 text-sm text-gray-600">
          <IconAlertTriangle className="w-4 h-4 text-warning-500 flex-shrink-0 mt-0.5" />
          <p>A taxa de <strong>{pctRate(data.ratePercent)}</strong> é um valor de <strong>referência</strong>
            {" "}({data.policyName}) — confirme o percentual real das apólices com o cliente. A taxa
            efetiva realmente paga no período foi <strong>{pctRate(data.effectiveRatePercent)}</strong>.</p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <KPIStatCard
          label="Valor da Mercadoria" memory={{ cardKey: "seguro.mercadoria", from, to, expected: data?.goodsValueTotal }} value={data ? data.goodsValueTotalFormatted : "—"}
          valueSub="carga transportada (CT-e)" status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Taxa Aplicada" memory={{ cardKey: "seguro.taxa", from, to, expected: data?.ratePercent }} value={data ? pctRate(data.ratePercent) : "—"}
          valueSub={data ? (data.rateSource === "policy" ? "apólice" : data.rateSource === "override" ? "simulação" : "—") : undefined}
          status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Seguro Estimado" memory={{ cardKey: "seguro.estimado", from, to, expected: data?.estimatedInsurance }} value={data && data.estimatedInsuranceFormatted ? data.estimatedInsuranceFormatted : "—"}
          valueSub="mercadoria × taxa" status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Seguro Pago (real)" memory={{ cardKey: "seguro.pago", from, to, expected: data?.actualCargoPaid }} value={data ? data.actualCargoPaidFormatted : "—"}
          valueSub="RCTR-C / carga" status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Taxa Efetiva Paga" memory={{ cardKey: "seguro.taxa-efetiva", from, to, expected: data?.effectiveRatePercent }} value={data ? pctRate(data.effectiveRatePercent) : "—"}
          valueSub="pago / mercadoria"
          status={data && data.gap != null ? (data.gap > 0 ? "warning" : "good") : "neutral"} loading={isLoading}
        />
      </div>

      {/* Ranking por placa: valor de mercadoria + seguro estimado */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-center gap-2">
          <IconShield className="w-4 h-4 text-gray-400" />
          <p className="text-sm font-semibold text-gray-900">Exposição por Veículo (valor de mercadoria)</p>
        </div>
        <div className="divide-y divide-surface-border">
          {isLoading ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
          ) : byPlate.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem carga no período.</p>
          ) : byPlate.slice(0, 20).map((p, i) => (
            <div key={p.plate} className="px-5 py-3 flex items-center gap-3">
              <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
              <IconTruck className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{p.plate}</p>
                <p className="text-xs text-gray-400">{fmtNumber(p.cteCount)} CT-es</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-gray-900">{p.goodsValueFormatted}</p>
                {p.estimatedFormatted && <p className="text-xs text-gray-400">seguro est. {p.estimatedFormatted}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
